import React, { useState, useEffect } from 'react';
import { marked } from 'marked';

interface ZoningDocModalProps {
  isOpen: boolean;
  onClose: () => void;
  backendUrl: string;
}

export function ZoningDocModal({ isOpen, onClose, backendUrl }: ZoningDocModalProps) {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setError(null);

    fetch(`${backendUrl}/api/zoning/docs`)
      .then(res => {
        if (!res.ok) throw new Error(`Chyba při načítání dokumentace (status ${res.status})`);
        return res.json();
      })
      .then(data => {
        if (data.html) {
          setHtmlContent(data.html);
        } else if (data.markdown) {
          setHtmlContent(marked.parse(data.markdown) as string);
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  }, [isOpen, backendUrl]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      backdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '24px'
    }} onClick={onClose}>
      <div 
        style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333333',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden'
        }} 
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: '1px solid #333333',
          backgroundColor: '#222222'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>📐</span>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#f3f4f6' }}>
              Dokumentace zónování místností (ZONING.md)
            </h3>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <a 
              href={`${backendUrl}/zoning-doc`} 
              target="_blank" 
              rel="noreferrer"
              style={{
                color: '#60a5fa',
                fontSize: '13px',
                textDecoration: 'none',
                padding: '4px 8px',
                border: '1px solid rgba(96, 165, 250, 0.3)',
                borderRadius: '4px'
              }}
            >
              ↗ Otevřít na nové záložce
            </a>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9ca3af',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px'
              }}
              title="Zavřít"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          color: '#e5e7eb',
          fontSize: '14px',
          lineHeight: '1.7',
          flex: 1
        }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px', gap: '12px', color: '#9ca3af' }}>
              <span>Načítám dokumentaci zónování...</span>
            </div>
          )}

          {error && (
            <div style={{ padding: '20px', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid #ef4444', borderRadius: '8px', color: '#fca5a5' }}>
              <strong>Chyba:</strong> {error}
            </div>
          )}

          {!loading && !error && (
            <div 
              className="markdown-doc-body"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
